/**
 * Wall-clock countdown helpers. setInterval(1000) alone drops sub-second
 * remainder on stop/start; anchoring to performance.now() keeps it.
 */

export interface CountdownAnchor {
    remainingAtStart: number;
    startedAtMs: number;
}

export const startCountdown = (remaining: number, nowMs = performance.now()): CountdownAnchor => ({
    remainingAtStart: remaining,
    startedAtMs: nowMs,
});

/** Remaining seconds (fractional) since the anchor was started. */
export const readCountdown = (anchor: CountdownAnchor, nowMs = performance.now()): number =>
    Math.max(0, anchor.remainingAtStart - (nowMs - anchor.startedAtMs) / 1000);

/** Whole minutes + seconds for event payloads / DB (no fractions). */
export const splitWholeSeconds = (totalSeconds: number): { minute: number; seconds: number } => {
    const whole = Math.max(0, Math.floor(totalSeconds));
    const minute = Math.floor(whole / 60);
    return { minute, seconds: whole - minute * 60 };
};

/** Desktop-style display: MM:SS (floored). */
export const formatClockMmSs = (totalSeconds: number): string => {
    const { minute, seconds } = splitWholeSeconds(totalSeconds);
    const m = minute < 10 ? `0${minute}` : String(minute);
    const s = seconds < 10 ? `0${seconds}` : String(seconds);
    return `${m}:${s}`;
};

/** Mobile main clock: MM:SS.t (tenths). */
export const formatClockMmSsTenths = (totalSeconds: number): string => {
    // Round to tenths first so 32.7 does not become 32.6999… → ".6".
    const t = Math.max(0, Math.round(totalSeconds * 10) / 10);
    const minute = Math.floor(t / 60);
    const sec = t - minute * 60;
    const whole = Math.floor(sec + 1e-9);
    const tenth = Math.min(9, Math.round((sec - whole) * 10));
    const m = minute < 10 ? `0${minute}` : String(minute);
    const s = whole < 10 ? `0${whole}` : String(whole);
    return `${m}:${s}.${tenth}`;
};
